import React, { useState, useEffect } from 'react';
import { Button } from '../../../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../../components/ui/dialog';
import { Download, FileText, X, Trash2 } from 'lucide-react';
import { TeamMemberDocument } from '../../../types';
import { useTeamMembersReduxHook } from '../../../hooks/useTeamMembersRedux';
import { Notification as customNotification } from '../../../../../components/ui/custom-notification';
import { ApproveButton } from '../../../../../components/ui/ApproveButton';

interface TeamMemberDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  documents: TeamMemberDocument[];
  onDownload: (document: TeamMemberDocument) => void;
  title?: string;
  teamMemberName?: string;
  projectId?: number;
}

export const TeamMemberDocumentModal: React.FC<TeamMemberDocumentModalProps> = ({
  isOpen,
  onClose,
  documents,
  onDownload,
  title = 'Team Member Certificates',
  teamMemberName,
  projectId
}) => {
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [certificateToDelete, setCertificateToDelete] = useState<TeamMemberDocument | null>(null);
  const { deleteTeamMemberCert, deleteLoading, error } = useTeamMembersReduxHook();
  const handleDownload = (doc: TeamMemberDocument) => {
    // If we have a file_url, open it directly in new tab (not download)
    if (doc.file_url) {
      window.open(doc.file_url, '_blank');
    } else {
      // Fallback to the onDownload callback if no file_url
      onDownload(doc);
    }
  };

  const handleDelete = (doc: TeamMemberDocument) => {
    setCertificateToDelete(doc);
    setIsDeleteModalVisible(true);
  };

  const handleConfirmDelete = () => {
    if (!certificateToDelete?.id) return;
    
    // Extract cert_id from the document ID (format: cert_17)
    const certId = certificateToDelete.id.replace('cert_', '');
    
    setIsDeleting(certificateToDelete.id);
    // Call the delete function - the saga will handle success/error notifications
    deleteTeamMemberCert(certId, projectId);
  };

  // Effect to handle deletion completion
  useEffect(() => {
    if (isDeleting && !deleteLoading && !error) {
      // Deletion completed successfully
      setIsDeleteModalVisible(false);
      setCertificateToDelete(null);
      setIsDeleting(null);
      
      // Close the main document modal and refresh team members after successful deletion
      console.log('Closing document modal and refreshing team members');
      onClose();
    } else if (isDeleting && error) {
      // Deletion failed
      console.error('Error deleting certificate:', error);
      setIsDeleting(null);
      // Error notification is already shown by the saga
    }
  }, [deleteLoading, error, isDeleting, onClose]);

  const handleCancelDelete = () => {
    setIsDeleteModalVisible(false);
    setCertificateToDelete(null);
  };




  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {teamMemberName ? `${teamMemberName}'s Documents (${documents.length} files)` : `${title} (${documents.length} files)`}
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4 max-h-96 overflow-y-auto">
          {documents.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No certificates available
            </div>
          ) : (
            <>

              
              <div className="space-y-3">
              {/* Show all documents/certificates in a list */}
              {documents.map((doc, index) => (
                <div
                  key={doc.id || index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <FileText className="h-5 w-5 text-gray-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {doc.fileName}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>{formatDate(doc.uploadDate)}</span>
                        <span className="capitalize">{doc.type?.toLowerCase().replace('_', ' ')}</span>
                        {/* Show document tags if available */}
                        {doc.document_tags && doc.document_tags.length > 0 && (
                          <span className="text-blue-600">
                            Tags: {doc.document_tags.join(', ')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      onClick={() => handleDownload(doc)}
                      className="flex items-center gap-2"
                      title={doc.file_url ? "Download Certificate" : "Certificate not available"}
                      disabled={!doc.file_url}
                    >
                      <Download className="h-4 w-4" />
                      Download
                    </Button>
                    <ApproveButton
                      docId={(doc as any).docId || parseInt(doc.id?.replace('cert_', '') || '0', 10)}
                      isApproved={(doc as any).approvalStatus === 'approved'}
                      apiType="teamMember"
                    />
                    <Button
                      size="sm"
                      onClick={() => handleDelete(doc)}
                      className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Delete Certificate"
                      disabled={isDeleting === doc.id}
                      variant="outline"
                    >
                      {isDeleting === doc.id ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                      {isDeleting === doc.id ? 'Deleting...' : 'Delete'}
                    </Button>
                  </div>
                </div>
              ))}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            className="flex items-center gap-2"
          >
            <X className="h-4 w-4" />
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Delete Confirmation Modal */}
    <Dialog open={isDeleteModalVisible} onOpenChange={setIsDeleteModalVisible}>
      <DialogContent className="max-w-[500px] p-6">
        <div className="flex justify-between items-center mb-6">
          <DialogTitle className="text-xl font-semibold flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-red-600" />
            Delete Certificate
          </DialogTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancelDelete}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="mb-6">
          <p className="text-gray-700 mb-3">
            Are you sure you want to <span className="font-semibold text-red-600">delete</span> the certificate document
            <span className="font-semibold text-red-600"> "{certificateToDelete?.fileName}"</span>?
          </p>
          
          {certificateToDelete && (
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-sm text-gray-600 mb-1">Certificate details:</p>
              <p className="text-sm text-gray-700">• File Name: {certificateToDelete.fileName}</p>
              <p className="text-sm text-gray-700">• Upload Date: {formatDate(certificateToDelete.uploadDate)}</p>
              <p className="text-sm text-gray-700">• Type: {certificateToDelete.type || 'Certificate'}</p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-3">
          <Button
            variant="outline"
            onClick={handleCancelDelete}
            disabled={isDeleting === certificateToDelete?.id}
            className="min-w-[100px]"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={isDeleting === certificateToDelete?.id}
            className="bg-red-600 hover:bg-red-700 text-white min-w-[100px]"
          >
            {isDeleting === certificateToDelete?.id ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Deleting...
              </div>
            ) : (
              'Delete'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}; 